import { hoursToSeconds } from 'date-fns';

import type { DeliverySlot } from '~/api/ETA';

import type { SlotDeliveryDetails } from './Delivery';

/**
 * Windows the customer can actually be given for self-collection.
 *
 * Mirrors the filter `useDeliverySlotsInfo` (the picker's shared hook, also used
 * by courier delivery) applies to `checkoutStore.deliverySlots`: `is_active`,
 * free capacity, an unexpired `cut_off_time`, and `delivery_min_time` against the
 * cart's backorder floor. Both filters must be kept in sync by hand — there is no
 * shared implementation — or the store can pre-select a window the picker would
 * not have offered, and the reverse.
 */
export const filterBookablePickupSlots = (
  slots: DeliverySlot[] | null,
  now: number,
  backorderFloor: number,
): DeliverySlot[] =>
  (slots ?? []).filter(
    (slot) =>
      slot.cut_off_time > now &&
      slot.is_active &&
      slot.remaining_capacity > 0 &&
      slot.delivery_min_time >= backorderFloor,
  );

/**
 * Earliest moment the cart can be handed over. `useSlots` derives it from the
 * *smallest* backorder lead time in the leg — its `sort().at(0)` takes the
 * minimum despite the local name — and the pickup path must use the identical
 * floor or the picker and the store disagree about which windows exist.
 */
export const backorderFloorFor = (
  leadTimesHours: number[],
  now: number,
): number =>
  now + hoursToSeconds(leadTimesHours.length ? Math.min(...leadTimesHours) : 0);

/**
 * The selection a self-collection leg carries. Built in one place so both
 * rebuild branches emit the same key order.
 */
export const pickupSlotSelection = (
  slot: DeliverySlot,
  isAutomatic: boolean,
): SlotDeliveryDetails => ({
  scheduleSlotId: slot.schedule_slot_id,
  currentDate: slot.current_date,
  ...(isAutomatic ? { isAutomatic: true } : {}),
});

/** Same window and same mode, compared field-wise so key order cannot fake a change. */
export const isSameSlotSelection = (
  a: SlotDeliveryDetails | undefined,
  b: SlotDeliveryDetails | undefined,
): boolean =>
  a?.scheduleSlotId === b?.scheduleSlotId &&
  a?.currentDate === b?.currentDate &&
  Boolean(a?.isAutomatic) === Boolean(b?.isAutomatic);
